/* ============================================================
   Card Registry — Atomic draggable card components
   Each entry represents a single, self-contained visual card
   that can be placed anywhere on the builder grid canvas.
   ============================================================ */

// ── Types ─────────────────────────────────────────────

export interface CardDef {
  /** Unique type key, e.g. 'service-card' */
  type: string
  /** Human label */
  label: string
  /** Icon (emoji) */
  icon: string
  /** Short description */
  description: string
  /** Category grouping */
  category: CardCategory
  /** Default column span (1-12 on the grid) */
  defaultColSpan: number
  /** Allowed column spans */
  allowedColSpans: number[]
  /** Whether this card is linked to DB entity data */
  entityType?: 'service' | 'stylist' | 'testimonial' | 'event' | 'image'
  /** Default data for new cards of this type */
  defaults: Record<string, string>
  /** Keyboard shortcut hint (displayed in component drawer) */
  shortcut?: string
}

export type CardCategory = 'content' | 'data' | 'layout' | 'media'

/** Per-block visual styling overrides */
export interface BlockStyles {
  backgroundColor?: string
  borderRadius?: number
  borderColor?: string
  borderWidth?: number
  padding?: number
  opacity?: number
  textColor?: string
  fontFamily?: string
  fontSize?: number
  fontWeight?: number
  textAlign?: 'left' | 'center' | 'right'
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'glow'
  animation?: 'none' | 'fadeIn' | 'slideUp' | 'slideLeft' | 'scaleIn' | 'bounce'
}

export interface CanvasBlock {
  /** Unique instance ID */
  id: string
  /** Card type key from CardDef.type */
  type: string
  /** Grid column span (1-12) */
  colSpan: number
  /** Content data for this card */
  data: Record<string, string>
  /** Per-block visual styling overrides */
  styles?: BlockStyles
  /** Linked entity ID (for data-bound cards) */
  entityId?: string
  /** Linked entity data (populated at runtime) */
  entityData?: Record<string, unknown>
  /** Whether this block is locked from editing/moving */
  locked?: boolean
  /** Custom CSS injected for this block */
  customCss?: string
  /** Conditional visibility rules */
  visibilityRules?: Array<{
    condition: 'auth' | 'screen-size' | 'data'
    operator: 'equals' | 'not-equals' | 'gte' | 'lte'
    value: string
  }>
  /** Animation configuration */
  animationConfig?: {
    preset: 'none' | 'fade-up' | 'slide-left' | 'slide-right' | 'scale-in' | 'bounce'
    duration: number
    delay: number
    trigger: 'on-appear' | 'on-scroll'
  }
  /** Responsive style overrides per breakpoint */
  responsiveStyles?: {
    tablet?: Partial<BlockStyles>
    mobile?: Partial<BlockStyles>
  }
}

// ── Card Definitions ──────────────────────────────────

export const CARD_TYPES: CardDef[] = [
  // ── Content cards ───────────────────────────
  {
    type: 'hero-banner',
    label: 'Hero',
    icon: '♡',
    description: 'Full-width headline with subtitle and CTA button',
    category: 'content',
    defaultColSpan: 12,
    allowedColSpans: [12],
    shortcut: 'H',
    defaults: {
      headline: 'Your Headline Here',
      subline: 'A brief description of your business',
      cta_text: 'Get Started',
      cta_link: '#',
    },
  },
  {
    type: 'text-block',
    label: 'Rich Text',
    icon: '☐',
    description: 'Heading and body paragraph',
    category: 'content',
    defaultColSpan: 6,
    allowedColSpans: [3, 4, 6, 8, 12],
    shortcut: 'T',
    defaults: {
      heading: 'Section Title',
      body: 'Add your content here. This text block supports rich formatting.',
    },
  },
  {
    type: 'heading',
    label: 'Heading',
    icon: '🔠',
    description: 'Standalone heading text',
    category: 'content',
    defaultColSpan: 12,
    allowedColSpans: [4, 6, 8, 12],
    defaults: {
      text: 'Section Heading',
      level: 'h2',
    },
  },
  {
    type: 'cta-block',
    label: 'Call to Action',
    icon: '☐',
    description: 'Call to action with headline and button',
    category: 'content',
    defaultColSpan: 12,
    allowedColSpans: [6, 8, 12],
    defaults: {
      headline: 'Ready to Get Started?',
      body: 'Book your appointment today.',
      cta_text: 'Book Now',
      cta_link: '/booking',
    },
  },

  // ── Data-bound cards ────────────────────────
  {
    type: 'service-card',
    label: 'Service Card',
    icon: '✂️',
    description: 'Individual service with name, price, and duration',
    category: 'data',
    defaultColSpan: 4,
    allowedColSpans: [3, 4, 6, 12],
    entityType: 'service',
    defaults: {
      name: 'Service Name',
      price: '50',
      duration: '60',
      description: 'Service description',
    },
  },
  {
    type: 'team-card',
    label: 'Team Member',
    icon: '👤',
    description: 'Team member photo card with name and title',
    category: 'data',
    defaultColSpan: 4,
    allowedColSpans: [3, 4, 6],
    entityType: 'stylist',
    defaults: {
      name: 'Team Member',
      title: 'Specialist',
    },
  },
  {
    type: 'stylist-flip-card',
    label: 'Stylist Flip Card',
    icon: '💈',
    description: '3D flip card with photo, bio, products, and color presets',
    category: 'data',
    defaultColSpan: 4,
    allowedColSpans: [4, 6],
    entityType: 'stylist',
    defaults: {
      name: 'Alex Rivera',
      title: 'Senior Colorist',
      description:
        "Passionate about creating personalized looks that reflect each client's unique style.",
      preset: 'default',
      bookingLink: '#',
    },
  },
  {
    type: 'testimonial-card',
    label: 'Testimonial',
    icon: '⭐',
    description: 'Client review with quote and attribution',
    category: 'data',
    defaultColSpan: 4,
    allowedColSpans: [4, 6, 12],
    entityType: 'testimonial',
    defaults: {
      quote: '"Amazing experience, I keep coming back!"',
      author_name: 'Happy Client',
      author_role: 'Regular Customer',
    },
  },
  {
    type: 'event-card',
    label: 'Event Card',
    icon: '📅',
    description: 'Single event with date, time, and details',
    category: 'data',
    defaultColSpan: 4,
    allowedColSpans: [4, 6, 12],
    entityType: 'event',
    defaults: {
      title: 'Upcoming Event',
      date: new Date().toISOString().split('T')[0],
      description: 'Event details go here',
    },
  },
  {
    type: 'looks-gallery',
    label: 'Looks Gallery',
    icon: '💇',
    description: 'Horizontal-scroll gallery of bookable looks from recipes',
    category: 'data',
    defaultColSpan: 12,
    allowedColSpans: [8, 12],
    defaults: {
      heading: 'Browse Looks',
      subheading: 'Get inspired by our curated gallery — find your look, then book it in one tap.',
      maxItems: '6',
    },
  },

  // ── Media cards ─────────────────────────────
  {
    type: 'image-block',
    label: 'Image',
    icon: '🖼️',
    description: 'Single image with optional caption',
    category: 'media',
    defaultColSpan: 6,
    allowedColSpans: [3, 4, 6, 8, 12],
    defaults: {
      src: '',
      alt: 'Image description',
      caption: '',
    },
  },
  {
    type: 'gallery-image',
    label: 'Gallery Image',
    icon: '📷',
    description: 'Image card for photo gallery',
    category: 'media',
    defaultColSpan: 4,
    allowedColSpans: [3, 4, 6],
    entityType: 'image',
    defaults: {
      src: '',
      alt: 'Gallery image',
    },
  },

  // ── Layout blocks ───────────────────────────
  {
    type: 'section',
    label: 'Section',
    icon: '☐',
    description: 'Container section wrapper',
    category: 'layout',
    defaultColSpan: 12,
    allowedColSpans: [12],
    shortcut: 'S',
    defaults: {},
  },
  {
    type: 'grid',
    label: 'Grid',
    icon: '⊞',
    description: 'Multi-column grid layout',
    category: 'layout',
    defaultColSpan: 12,
    allowedColSpans: [12],
    shortcut: 'G',
    defaults: {},
  },
  {
    type: 'columns',
    label: 'Columns',
    icon: '☰',
    description: 'Side-by-side column layout',
    category: 'layout',
    defaultColSpan: 12,
    allowedColSpans: [12],
    shortcut: 'C',
    defaults: {},
  },
  {
    type: 'spacer',
    label: 'Spacer',
    icon: '↕',
    description: 'Vertical spacing between elements',
    category: 'layout',
    defaultColSpan: 12,
    allowedColSpans: [12],
    defaults: { height: '48' },
  },
  {
    type: 'divider',
    label: 'Divider',
    icon: '—',
    description: 'Horizontal separator line',
    category: 'layout',
    defaultColSpan: 12,
    allowedColSpans: [6, 8, 12],
    defaults: {},
  },
]

// ── Lookup helpers ────────────────────────────────────

/**
 * Look up a card definition by its unique `type`.
 *
 * @param type - Card type identifier (e.g., `hero`, `form`, `table`)
 * @returns Registered card definition or `undefined` when missing
 */
export function getCardDef(type: string): CardDef | undefined {
  return CARD_TYPES.find((c) => c.type === type)
}

/**
 * Filter card definitions by category for UI tooling or palette rendering.
 *
 * @param category - Card category (layout, content, data, or media)
 * @returns Array of card definitions belonging to the given category
 */
export function getCardsByCategory(category: CardCategory): CardDef[] {
  return CARD_TYPES.filter((c) => c.category === category)
}

export const CARD_CATEGORIES: { key: CardCategory; label: string; icon: string }[] = [
  { key: 'layout', label: 'Layout', icon: '📐' },
  { key: 'content', label: 'Content', icon: '📄' },
  { key: 'data', label: 'Data Cards', icon: '🗃️' },
  { key: 'media', label: 'Media', icon: '🖼️' },
]

/** Create a new CanvasBlock from a card definition */
export function createBlock(type: string, overrides?: Partial<CanvasBlock>): CanvasBlock {
  const def = getCardDef(type)
  if (!def) throw new Error(`Unknown card type: ${type}`)

  return {
    id: `block-${crypto.randomUUID().slice(0, 8)}`,
    type,
    colSpan: def.defaultColSpan,
    data: { ...def.defaults },
    ...overrides,
  }
}
